import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useVendorMode } from "@/contexts/VendorModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Settings, MapPin, Camera, Battery, ChevronRight, Bell, Mail, Link2, Utensils, Gift, CreditCard, QrCode, Share2, Sparkles, Clock, Shield, HelpCircle, RotateCcw, Trophy, Globe, Smartphone, ExternalLink, Loader2, Save } from "lucide-react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import VendorPOSSettings from "@/components/vendor/VendorPOSSettings";
import DoublePunchConfig from "@/components/vendor/DoublePunchConfig";
import TipOfTheDay from "@/components/shared/TipOfTheDay";

import VendorAvailabilityCalendar from "@/components/vendor/VendorAvailabilityCalendar";

/**
 * VendorSettings — basic structure only.
 * Full settings populate as features are built in subsequent parts.
 *
 * TODO — vendor onboarding Joyride tour added here in final vendor release.
 */
export default function VendorSettings() {
  const { updateModeDefault } = useVendorMode();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [modeDefault, setModeDefault] = useState("always_vendor");
  const [gpsActive, setGpsActive] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState(null);
  const [trialReminders, setTrialReminders] = useState(true);
  const [licenseReminders, setLicenseReminders] = useState(true);
  const [businessId, setBusinessId] = useState(null);
  const [noticeMinutes, setNoticeMinutes] = useState(15);
  const [punchMode, setPunchMode] = useState("trust_based");
  const [compensation, setCompensation] = useState(false);
  const [wizardLoaded, setWizardLoaded] = useState(false);
  const [extAppUrl, setExtAppUrl] = useState("");
  const [extWebUrl, setExtWebUrl] = useState("");
  const [extLinksSaving, setExtLinksSaving] = useState(false);
  // v1.0.0 Gate 6 Item 1 — Google Business Profile claim flag
  const [gbpClaimed, setGbpClaimed] = useState(false);
  const [gbpSaving, setGbpSaving] = useState(false);

  // Load external links
  useEffect(() => {
    axios.get(`${API}/vendor/external-links`).then(res => {
      setExtAppUrl(res.data.app_store_url || "");
      setExtWebUrl(res.data.website_url || "");
    }).catch(() => {});
  }, []);

  // v1.0.0 Gate 6 — load google_business_claimed flag from current user
  useEffect(() => {
    axios.get(`${API}/auth/me`).then(res => {
      setGbpClaimed(!!res.data?.google_business_claimed);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Check GPS permission
    if (navigator.geolocation) {
      navigator.permissions?.query({ name: "geolocation" }).then((result) => {
        setGpsActive(result.state === "granted");
      }).catch(() => {});
    }
    // Check camera permission
    navigator.permissions?.query({ name: "camera" }).then((result) => {
      setCameraActive(result.state === "granted");
    }).catch(() => {});
    // Check battery
    if (navigator.getBattery) {
      navigator.getBattery().then((bat) => {
        setBatteryLevel(Math.round(bat.level * 100));
      }).catch(() => {});
    }
    // Load email preferences
    axios.get(`${API}/vendor/email-prefs`).then(res => {
      setTrialReminders(res.data.trial_reminders ?? true);
      setLicenseReminders(res.data.license_reminders ?? true);
    }).catch(() => {});
    // Load vendor dashboard to get business_id
    axios.get(`${API}/vendor/dashboard`).then(res => {
      const bid = res.data.business_id || null;
      setBusinessId(bid);
      // Load wizard settings from dashboard response
      if (res.data.default_notice_minutes != null) setNoticeMinutes(res.data.default_notice_minutes);
      if (res.data.sold_out_compensation_enabled != null) setCompensation(res.data.sold_out_compensation_enabled);
      setWizardLoaded(true);
      if (bid) {
        // Load punch settings
        axios.get(`${API}/punchcard/settings/${bid}`).then(pRes => {
          if (pRes.data.confirmation_mode) setPunchMode(pRes.data.confirmation_mode);
        }).catch(() => {});
      }
    }).catch(() => { setWizardLoaded(true); });
  }, []);

  // Auto-scroll to punch card section when navigated with ?scroll=punch
  useEffect(() => {
    if (searchParams.get("scroll") === "punch") {
      const timer = setTimeout(() => {
        const el = document.querySelector('[data-testid="double-punch-section"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.style.transition = "box-shadow 0.3s";
          el.style.boxShadow = "0 0 20px rgba(245,158,11,0.4)";
          setTimeout(() => { el.style.boxShadow = "none"; }, 2500);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handleModeChange = (val) => {
    setModeDefault(val);
    updateModeDefault(val);
  };

  const statusColor = () => {
    if (gpsActive && cameraActive) return "bg-[#22C55E]";
    if (gpsActive || cameraActive) return "bg-amber-400";
    return "bg-red-500";
  };

  const now = new Date();
  const localTime = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZoneName: "short" });

  return (
    <div className="min-h-screen px-4 pt-14 pb-24" data-testid="vendor-settings">
      <TipOfTheDay pageKey="vendor_settings" />
      <h1 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
        <Settings className="w-5 h-5 text-[#F59E0B]" /> Vendor Settings
      </h1>

      {/* Profile Shortcuts */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="profile-shortcuts-section">
        <p className="text-xs font-semibold text-white/80 mb-3">Manage Your Profile</p>
        <div className="space-y-1.5">
          {[
            { label: "Smart Menu", icon: Sparkles, route: "/vendor/smart-menu" },
            { label: "Truck Info", icon: Utensils, route: "/vendor/truck-info" },
            { label: "Birthday Special", icon: Gift, route: "/vendor/birthday-card" },
            { label: "Menu", icon: Utensils, route: "/vendor/menu" },
            { label: "Photos", icon: Camera, route: "/vendor/photos" },
            { label: "Punch Card", icon: CreditCard, route: "/vendor/punch-card" },
            { label: "Social Media", icon: Share2, route: "/vendor/social" },
            { label: "Weekly Schedule", icon: Settings, route: "/vendor/schedule" },
          ].map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.route}
                onClick={() => navigate(item.route)}
                className="w-full flex items-center gap-3 p-3 bg-[#1C1C1E] rounded-lg border border-white/5 hover:border-[#F59E0B]/20 transition"
                data-testid={`settings-shortcut-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-xs text-white/70 flex-1 text-left">{item.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-white/20" />
              </button>
            );
          })}
        </div>
      </section>

      {/* Achievements */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="achievements-section">
        <p className="text-xs font-semibold text-white/80 mb-3">Achievements</p>
        <button
          onClick={() => navigate("/vendor/achievements")}
          className="w-full flex items-center gap-3 p-3 bg-[#1C1C1E] rounded-lg border border-white/5 hover:border-[#F59E0B]/20 transition"
          data-testid="settings-shortcut-achievements"
        >
          <Trophy className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-xs text-white/70 flex-1 text-left">Milestones & Leaderboard</span>
          <ChevronRight className="w-3.5 h-3.5 text-white/20" />
        </button>
      </section>

      {/* Schedule & Availability */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="availability-section">
        <VendorAvailabilityCalendar />
      </section>

      {/* Connected Review Platforms */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="review-platforms-section">
        <p className="text-xs font-semibold text-white/80 mb-3">Connected Review Platforms</p>
        <div className="space-y-2">
          {[
            { platform: "yelp", name: "Yelp", note: null, oauth: true },
            { platform: "google-business", name: "Google Business", note: "Requires Google Business Profile claimed for your truck", oauth: true },
            { platform: "facebook", name: "Facebook", note: "Requires a Facebook Page for your truck", oauth: true },
            { platform: "tripadvisor", name: "TripAdvisor", note: null, oauth: false },
            { platform: "reddit", name: "Reddit", note: null, oauth: false },
          ].map(p => (
            <div key={p.platform} className="flex items-center gap-3 p-3 bg-[#1C1C1E] rounded-lg border border-white/5" data-testid={`review-platform-${p.platform}`}>
              <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-[10px] font-bold text-white/40">
                {p.name[0]}
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-white">{p.name}</p>
                {p.oauth ? (
                  <p className="text-[10px] text-white/30">Not Connected</p>
                ) : (
                  <p className="text-[10px] text-green-400/60">Monitor Only — We watch for mentions</p>
                )}
                {p.note && <p className="text-[9px] text-white/20 mt-0.5">{p.note}</p>}
              </div>
              {p.oauth ? (
                <button className="text-[10px] text-[#F59E0B] font-medium">Connect</button>
              ) : (
                <span className="text-[10px] text-white/20">Auto</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* External Links */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="external-links-section">
        <p className="text-xs font-semibold text-white/80 mb-3">External Links</p>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">App Store Link</label>
            <div className="flex items-center gap-2 bg-[#1C1C1E] rounded-lg px-3 py-2 border border-white/5">
              <Smartphone className="w-4 h-4 text-white/30 shrink-0" />
              <input
                value={extAppUrl}
                onChange={e => setExtAppUrl(e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=..."
                className="flex-1 bg-transparent text-xs text-white/80 outline-none placeholder-white/20"
                data-testid="ext-app-url-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Website URL</label>
            <div className="flex items-center gap-2 bg-[#1C1C1E] rounded-lg px-3 py-2 border border-white/5">
              <Globe className="w-4 h-4 text-white/30 shrink-0" />
              <input
                value={extWebUrl}
                onChange={e => setExtWebUrl(e.target.value)}
                placeholder="https://www.yourtruck.com"
                className="flex-1 bg-transparent text-xs text-white/80 outline-none placeholder-white/20"
                data-testid="ext-web-url-input"
              />
            </div>
          </div>
          <button
            onClick={async () => {
              setExtLinksSaving(true);
              try {
                await axios.put(`${API}/vendor/external-links`, { app_store_url: extAppUrl.trim() || null, website_url: extWebUrl.trim() || null });
                toast.success("External links saved");
              } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
              finally { setExtLinksSaving(false); }
            }}
            disabled={extLinksSaving}
            className="w-full py-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-bold hover:bg-[#F59E0B]/20 transition disabled:opacity-40 flex items-center justify-center gap-1"
            data-testid="ext-links-save-btn"
          >
            {extLinksSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save External Links
          </button>
        </div>
      </section>

      {/* v1.0.0 Gate 6 Item 1 — Google Business Profile */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="gbp-section">
        <p className="text-xs font-semibold text-white/80 mb-2">Google Business Profile</p>
        <p className="text-[10px] text-white/40 mb-3 leading-relaxed">
          A claimed profile means your hours, photos, and birthday offers show up correctly on Google Maps and Google Search.
        </p>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="gbp-claimed-toggle">
          <input
            type="checkbox"
            checked={gbpClaimed}
            onChange={async (e) => {
              const next = e.target.checked;
              setGbpClaimed(next);
              setGbpSaving(true);
              try {
                await axios.patch(`${API}/auth/me`, { google_business_claimed: next });
                toast.success(next ? "Marked as claimed" : "Marked as unclaimed");
              } catch {
                toast.error("Couldn't save");
                setGbpClaimed(!next);
              } finally {
                setGbpSaving(false);
              }
            }}
            disabled={gbpSaving}
            className="w-4 h-4 rounded"
          />
          <span className="text-xs text-white/70">
            My Google Business Profile is claimed
          </span>
        </label>
        {!gbpClaimed && (
          <a
            href="https://www.google.com/maps"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#4285F4] font-semibold"
            data-testid="gbp-open-maps-link"
          >
            Open Google Maps →
          </a>
        )}
      </section>

      {/* Default mode on open */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="mode-default-section">
        <p className="text-xs font-semibold text-white/80 mb-3">Default mode on open</p>
        {[
          { val: "always_vendor", label: "Always open Vendor Mode" },
          { val: "always_user", label: "Always open User Mode" },
          { val: "last_used", label: "Return to last used" },
        ].map(opt => (
          <button
            key={opt.val}
            onClick={() => handleModeChange(opt.val)}
            className={`w-full flex items-center justify-between p-3 rounded-lg mb-1.5 transition ${
              modeDefault === opt.val
                ? "bg-[#F59E0B]/10 border border-[#F59E0B]/30"
                : "bg-[#1C1C1E] border border-transparent hover:border-white/10"
            }`}
            data-testid={`mode-option-${opt.val}`}
          >
            <span className={`text-sm ${modeDefault === opt.val ? "text-[#F59E0B] font-semibold" : "text-white/60"}`}>
              {opt.label}
            </span>
            {modeDefault === opt.val && <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />}
          </button>
        ))}
      </section>

      {/* GPS and Camera status */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="device-status-section">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-2.5 h-2.5 rounded-full ${statusColor()}`} data-testid="device-status-indicator" />
          <p className="text-xs font-semibold text-white/80">Device Status</p>
        </div>

        {/* First entry explanation */}
        <div className="bg-[#1C1C1E] rounded-lg p-3 mb-3 border border-white/5">
          <p className="text-[11px] text-[#EBEBF5]/60 leading-relaxed">
            <strong className="text-[#F59E0B]">GPS</strong> keeps your customers informed of your exact location so they can find you.{" "}
            <strong className="text-[#F59E0B]">Camera</strong> is required for QR scanning, business license verification, and menu and truck photo uploads.
            Without both active, vendor mode cannot function correctly.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-[#1C1C1E] rounded-lg">
            <div className="flex items-center gap-2">
              <MapPin className={`w-4 h-4 ${gpsActive ? "text-[#22C55E]" : "text-red-400"}`} />
              <span className="text-xs text-white/70">GPS</span>
            </div>
            <span className={`text-[10px] font-semibold ${gpsActive ? "text-[#22C55E]" : "text-red-400"}`} data-testid="gps-status">
              {gpsActive ? "Active" : "Off"}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-[#1C1C1E] rounded-lg">
            <div className="flex items-center gap-2">
              <Camera className={`w-4 h-4 ${cameraActive ? "text-[#22C55E]" : "text-red-400"}`} />
              <span className="text-xs text-white/70">Camera</span>
            </div>
            <span className={`text-[10px] font-semibold ${cameraActive ? "text-[#22C55E]" : "text-red-400"}`} data-testid="camera-status">
              {cameraActive ? "Active" : "Off"}
            </span>
          </div>

          {(!gpsActive || !cameraActive) && (
            <button
              onClick={() => { /* Device settings — not accessible from web */ }}
              className="w-full text-center py-2 text-[11px] text-[#F59E0B] font-semibold hover:underline"
              data-testid="open-device-settings"
            >
              Enable in device settings <ChevronRight className="inline w-3 h-3" />
            </button>
          )}
        </div>
      </section>

      {/* Battery level */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="battery-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Battery className={`w-4 h-4 ${batteryLevel !== null && batteryLevel < 30 ? "text-red-400" : "text-[#22C55E]"}`} />
            <span className="text-xs text-white/70">Battery</span>
          </div>
          <span className={`text-xs font-semibold ${batteryLevel !== null && batteryLevel < 30 ? "text-red-400" : "text-white/60"}`} data-testid="battery-level">
            {batteryLevel !== null ? `${batteryLevel}%` : "—"}
          </span>
        </div>
        {batteryLevel !== null && batteryLevel < 30 && (
          <p className="text-[10px] text-red-400/80 mt-2" data-testid="battery-warning">
            Make sure your device is charged before going live. Your customers are counting on you.
          </p>
        )}
      </section>

      {/* Go-Live Preferences (from wizard) */}
      {wizardLoaded && (
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="golive-prefs-section">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-xs font-bold text-white/70">Go-Live Preferences</span>
        </div>
        {/* Notice Time */}
        <div className="mb-3">
          <p className="text-[11px] text-white/50 mb-2">Follower notification lead time</p>
          <div className="flex gap-2 flex-wrap">
            {[5, 10, 15, 20, 30].map(m => (
              <button
                key={m}
                onClick={async () => {
                  setNoticeMinutes(m);
                  try {
                    await axios.post(`${API}/vendor/setup-wizard`, { default_notice_minutes: m, punch_confirmation_mode: punchMode, sold_out_compensation_enabled: compensation });
                  } catch { toast.error("Failed to save"); }
                }}
                className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-xs font-bold transition ${noticeMinutes === m ? "bg-[#F59E0B] text-[#1C1C1E]" : "bg-[#1C1C1E] text-white/50 border border-white/10"}`}
                data-testid={`notice-time-${m}`}
              >
                {m}m
              </button>
            ))}
          </div>
        </div>
        {/* Punch Confirmation Mode */}
        <div className="mb-3">
          <p className="text-[11px] text-white/50 mb-2">Punch card confirmation mode</p>
          <div className="space-y-1.5">
            {[
              { val: "trust_based", label: "Trust-based", desc: "Auto-confirms after dwell time" },
              { val: "return_to_zone", label: "Return to zone", desc: "Confirms when customer returns to pick up" },
              { val: "tap_to_confirm", label: "Tap to confirm", desc: "You manually approve each punch" },
            ].map(opt => (
              <button
                key={opt.val}
                onClick={async () => {
                  setPunchMode(opt.val);
                  try {
                    await axios.post(`${API}/vendor/setup-wizard`, { default_notice_minutes: noticeMinutes, punch_confirmation_mode: opt.val, sold_out_compensation_enabled: compensation });
                    await axios.put(`${API}/punchcard/settings`, { business_id: businessId, confirmation_mode: opt.val });
                    toast.success("Saved");
                  } catch { toast.error("Failed to save"); }
                }}
                className={`w-full text-left p-3 min-h-[44px] rounded-lg transition ${punchMode === opt.val ? "bg-[#F59E0B]/10 border border-[#F59E0B]/40" : "bg-[#1C1C1E] border border-white/5"}`}
                data-testid={`punch-mode-${opt.val}`}
              >
                <p className={`text-xs font-bold ${punchMode === opt.val ? "text-[#F59E0B]" : "text-white/70"}`}>{opt.label}</p>
                <p className="text-[10px] text-white/40 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>
        {/* Sold-out Compensation Toggle */}
        <div>
          <label className="flex items-center justify-between cursor-pointer min-h-[44px]" data-testid="compensation-toggle">
            <div>
              <p className="text-[11px] text-white/60">Sold-out compensation punch</p>
              <p className="text-[9px] text-white/30">Auto-award a free punch when you mark an item sold out</p>
            </div>
            <input
              type="checkbox"
              checked={compensation}
              onChange={async (e) => {
                const val = e.target.checked;
                setCompensation(val);
                try {
                  await axios.post(`${API}/vendor/setup-wizard`, { default_notice_minutes: noticeMinutes, punch_confirmation_mode: punchMode, sold_out_compensation_enabled: val });
                  toast.success("Saved");
                } catch { toast.error("Failed to save"); }
              }}
              className="w-5 h-5 rounded text-[#F59E0B] border-white/20"
            />
          </label>
        </div>
      </section>
      )}

      {/* Email Preferences (Part 22L) */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="email-prefs-section">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-xs font-bold text-white/70">Email Reminders</span>
        </div>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer" data-testid="trial-reminders-toggle">
            <span className="text-[11px] text-white/60">Trial expiry reminders</span>
            <input
              type="checkbox"
              checked={trialReminders}
              onChange={async (e) => {
                const val = e.target.checked;
                setTrialReminders(val);
                try {
                  await axios.post(`${API}/vendor/update-email-prefs`, { trial_reminders: val });
                } catch { toast.error("Could not update"); }
              }}
              className="w-4 h-4 rounded text-[#F59E0B] border-white/20"
            />
          </label>
          <label className="flex items-center justify-between cursor-pointer" data-testid="license-reminders-toggle">
            <span className="text-[11px] text-white/60">License expiry reminders</span>
            <input
              type="checkbox"
              checked={licenseReminders}
              onChange={async (e) => {
                const val = e.target.checked;
                setLicenseReminders(val);
                try {
                  await axios.post(`${API}/vendor/update-email-prefs`, { license_reminders: val });
                } catch { toast.error("Could not update"); }
              }}
              className="w-4 h-4 rounded text-[#F59E0B] border-white/20"
            />
          </label>
        </div>
      </section>

      {/* Tour & Help */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="tour-help-section">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-xs font-bold text-white/70">Tour & Help</span>
        </div>
        <button
          onClick={async () => {
            try {
              await axios.put(`${API}/vendor/tour-complete`, { reset: true });
            } catch { /* silent */ }
            navigate("/vendor");
          }}
          className="w-full flex items-center gap-3 p-3 bg-[#1C1C1E] rounded-lg border border-white/5 hover:border-[#F59E0B]/20 transition"
          data-testid="replay-tour-btn"
        >
          <RotateCcw className="w-4 h-4 text-[#F59E0B]" />
          <span className="text-xs text-white/70 flex-1 text-left">Replay Vendor Tour</span>
          <ChevronRight className="w-3.5 h-3.5 text-white/20" />
        </button>
        <p className="text-[10px] text-white/30 mt-2 px-1">Revisit the guided tour of your vendor command center.</p>
      </section>

      {/* Local timezone display */}
      <section className="bg-[#2C2C2E] rounded-xl p-4" data-testid="timezone-section">
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/70">Local time</span>
          <span className="text-xs font-mono text-[#F59E0B]" data-testid="local-time">{localTime}</span>
        </div>
        <p className="text-[10px] text-white/30 mt-1">All times displayed in your device timezone</p>
      </section>

      {/* POS Connections (Task 5) */}
      <section className="mt-3" data-testid="pos-connections-section">
        <VendorPOSSettings />
      </section>

      {/* Double Punch Days */}
      {businessId && (
        <section className="mt-3" data-testid="double-punch-section">
          <DoublePunchConfig businessId={businessId} />
        </section>
      )}
    </div>
  );
}
